/**
 * Utilidades de fecha/hora centralizadas.
 *
 * Regla del sistema:
 *  - El backend guarda instantes en UTC (ISO 8601 con offset).
 *  - Se MUESTRAN en la zona horaria del centro de trabajo (IANA, p. ej.
 *    "America/Mexico_City"), que la API adjunta como `zona_horaria` en cada fila.
 *  - Si no se conoce la zona, se cae a la del navegador.
 *
 * El backend hace lo mismo en backend/tz_utils.py — cualquier cambio de criterio
 * debe reflejarse en ambos lados.
 */

const LOCALE = "es-MX";

// Normaliza un ISO que podría venir sin sufijo de zona (registros legados) a UTC.
function normalizeIso(iso) {
  if (!iso) return iso;
  return /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
}

/** ISO → Date (instante). null si no es parseable. */
export function parseInstant(iso) {
  if (!iso) return null;
  const d = new Date(normalizeIso(iso));
  return Number.isNaN(d.getTime()) ? null : d;
}

function withTz(tz, opts) {
  return tz ? { ...opts, timeZone: tz } : opts;
}

/** Instante UTC → "HH:mm" (24 h) en la zona dada (o la del navegador). */
export function fmtHora(iso, tz) {
  const d = parseInstant(iso);
  if (!d) return "—";
  return d.toLocaleTimeString(LOCALE, withTz(tz, { hour: "2-digit", minute: "2-digit", hour12: false }));
}

/** Instante UTC → "HH:mm:ss" en la zona dada. */
export function fmtHoraSeg(iso, tz) {
  const d = parseInstant(iso);
  if (!d) return "—";
  return d.toLocaleTimeString(LOCALE, withTz(tz, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }));
}

/** Instante UTC → "YYYY-MM-DD HH:mm:ss" en la zona dada — para bitácoras. */
export function fmtFechaHora(iso, tz) {
  const d = parseInstant(iso);
  if (!d) return "—";
  // sv-SE da formato ISO-like "YYYY-MM-DD HH:mm:ss".
  return d.toLocaleString("sv-SE", withTz(tz, { dateStyle: "short", timeStyle: "medium" }));
}

/** Instante UTC → fecha larga legible ("martes, 2 de septiembre de 2026") en la zona dada. */
export function fmtFechaLarga(iso, tz) {
  const d = parseInstant(iso);
  if (!d) return "—";
  return d.toLocaleDateString(LOCALE, withTz(tz, { weekday: "long", year: "numeric", month: "long", day: "numeric" }));
}

/**
 * "Hoy" como "YYYY-MM-DD" en la zona dada (o la del navegador).
 * Reemplaza el antipatrón `new Date().toISOString().slice(0, 10)`, que da la
 * fecha en UTC y de noche (hora local) ya "salta" al día siguiente.
 */
export function hoyISO(tz) {
  return new Date().toLocaleDateString("en-CA", tz ? { timeZone: tz } : undefined);
}

/** Igual que hoyISO() pero N días atrás. */
export function fechaISOhaceDias(dias, tz) {
  const d = new Date(Date.now() - dias * 86400000);
  return d.toLocaleDateString("en-CA", tz ? { timeZone: tz } : undefined);
}

// ---- <input type="datetime-local"> <-> instante UTC, respetando una zona IANA ----

// Offset (ms) de una zona IANA en un instante dado: (hora de pared en tz) - UTC.
function tzOffsetMs(date, tz) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const p = {};
  for (const { type, value } of dtf.formatToParts(date)) p[type] = value;
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUTC - date.getTime();
}

/**
 * Instante UTC (ISO) → valor para `<input type="datetime-local">`
 * ("YYYY-MM-DDTHH:mm") mostrando la hora de pared en `tz`.
 */
export function isoADatetimeLocal(iso, tz) {
  const d = parseInstant(iso);
  if (!d) return "";
  const s = d.toLocaleString("sv-SE", withTz(tz, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }));
  return s.replace(" ", "T").slice(0, 16);
}

/**
 * Valor de `<input type="datetime-local">` ("YYYY-MM-DDTHH:mm"), interpretado
 * como hora de pared en `tz`, → instante UTC (ISO con "Z").
 * Sin `tz`, usa la zona del navegador (comportamiento nativo de `new Date`).
 */
export function datetimeLocalAIso(valor, tz) {
  if (!valor) return null;
  if (!tz) return new Date(valor).toISOString();
  const [fecha, hora] = valor.split("T");
  const [y, mo, da] = fecha.split("-").map(Number);
  const [h, mi] = (hora || "0:0").split(":").map(Number);
  const naiveUTC = Date.UTC(y, mo - 1, da, h, mi);
  // Dos pasadas: la segunda corrige el borde de cambio de horario de verano.
  let off = tzOffsetMs(new Date(naiveUTC), tz);
  off = tzOffsetMs(new Date(naiveUTC - off), tz);
  return new Date(naiveUTC - off).toISOString();
}
